import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import yup from '../../libs/yup/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import TextInput from '../../components/form/TextInput';
import Swal from 'sweetalert2';
import { updateBankAccount, getBankAccountDetail } from '../../api/sportaloka-client';
import { AxiosError } from 'axios';

const defaultValues = {
  account_name: '',
  account_number: '',
  bank_name: 'BCA'
};

const schema = yup.object().shape({
  account_name: yup.string().label('Nama Pemiliki Rekening').required('${label} wajib diisi'),
  account_number: yup.string().label('Nomor Rekening').required('${label} wajib diisi'),
  bank_name: yup.string().label('Jenis Promosi').required('${label} wajib dipilih')
});

const DetailBankAccount = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const form = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = async (val) => {
    try {
      setLoading(true);
      await updateBankAccount(id, val);
      navigate('/bank-account');
      Swal.fire({
        title: 'Berhasil!',
        text: `Rekening berhasil ditambahkan`,
        icon: 'success',
        confirmButtonText: 'Tutup'
      });
      setLoading(false);
    } catch (error) {
      let message = 'Periksa koneksi internet anda dan coba kembali';

      if (error instanceof AxiosError && error.response.data.code === '101002') {
        message = 'Rekening sudah pernah didaftarkan';
      }

      Swal.fire({
        title: 'Gagal!',
        text: message,
        icon: 'error',
        confirmButtonText: 'Tutup'
      });
      setLoading(false);
    }
  };

  const getData = async () => {
    try {
      setFetchError(false);
      setFetchLoading(true);
      const response = await getBankAccountDetail(id);
      form.reset({
        account_name: response.data.data.account_name,
        bank_name: response.data.data.bank_name,
        account_number: response.data.data.account_number
      });
      setFetchLoading(false);
    } catch (error) {
      setFetchError(true);
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="row y-gap-20 justify-between items-end pb-60 lg:pb-40 md:pb-32">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="text-30 lh-14 fw-600">Detail Rekening Bank</h1>
            <div className="d-flex">
              <Link to="/bank-account" className="button -md bg-white h-50 text-dark mr-20">
                <i className="fa fa-arrow-left mr-10" />
                Kembali
              </Link>

              <button disabled={loading} className="button -md bg-blue-1 h-50 text-white">
                {!loading && <i className="fa fa-plus text-16 mr-10" />}
                {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
              </button>
            </div>
          </div>
          <div className="py-30 px-30 rounded-4 bg-white shadow-3 mt-20">
            {fetchLoading && !fetchError && <div className="fw-500 text-center">Loading...</div>}
            {!fetchLoading && fetchError && (
              <div className="fw-500 text-center">
                Gagal mendapatkan data{' '}
                <span className="ml-4 text-danger cursor-pointer" onClick={getData}>
                  Coba Kembali
                </span>
              </div>
            )}
            {!fetchLoading && !fetchError && (
              <div className="row">
                <div className="col-12">
                  <Controller
                    control={form.control}
                    name="bank_name"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div className="form-group">
                        <label>
                          Nama Bank <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}>
                          <option value="BCA">Bank Central Asia (BCA)</option>
                          <option value="MANDIRI">Bank Mandiri</option>
                          <option value="BNI">Bank Negara Indonesia (BNI)</option>
                          <option value="BRI">Bank Rakyat Indonesia (BRI)</option>
                          <option value="PERMATA">Bank Permata</option>
                          <option value="CIMB">Bank CIMB</option>
                        </select>
                      </div>
                    )}
                  />
                </div>
                <div className="col-6">
                  <Controller
                    control={form.control}
                    name="account_name"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        disabled={loading}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        type="text"
                        isRequired={true}
                        label="Nama Pemilik Rekening"
                        isError={Boolean(form.formState.errors.account_name)}
                        errorMessage={
                          Boolean(form.formState.errors.account_name) &&
                          form.formState.errors.account_name.message
                        }
                      />
                    )}
                  />
                </div>
                <div className="col-6">
                  <Controller
                    control={form.control}
                    name="account_number"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        disabled={loading}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        type="text"
                        isRequired={true}
                        label="Nomor Rekening"
                        isError={Boolean(form.formState.errors.account_number)}
                        errorMessage={
                          Boolean(form.formState.errors.account_number) &&
                          form.formState.errors.account_number.message
                        }
                      />
                    )}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default DetailBankAccount;
